import React from "react";
import { keyBy, merge, values } from 'lodash';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import {
  interpolateDivergant,
  toIndex,
  toPercentage,
} from "@/utils";
import { HelpBox } from "@/components/atoms";
import { InsightResult } from "@/types";

interface Mode {
  name: string;
  label: string;
  icon: React.ReactElement;
}

interface ModeCardProps {
  title: string,
  modes: Mode[];
  variables: InsightResult[];
  score: string;
  minValue?: number;
  maxValue?: number;
}

const ModeCard: React.FC<ModeCardProps> = ({
  title,
  modes,
  variables,
  score,
  minValue,
  maxValue,
}) => {
  const theme = useTheme();

  const formatFunc = score === "rate" ? toPercentage : toIndex;

  const calcPercentage = (value: number) => {
    const mx = maxValue ? maxValue : 1;
    const mn = minValue ? minValue : 0;
    return Math.max(0.01, (value - mn) / (mx - mn));
  };

  const modeScores = values(merge(keyBy(modes, 'name'), keyBy(variables, 'tag_name')));

  return (
    <Card
      sx={{
        border: 1,
        borderRadius: 1,
        borderColor:theme.palette.divider,
        // width: "230px",
        height: "166px",
      }}
    >
      <CardHeader title={title} slotProps={{ title: { variant: "body1" } }} />
      <CardContent
        sx={{
          pt: 0,
          pb: 2,
          "&:last-child": { pb: 2 },
        }}
      >
        <Stack direction="row" spacing={1}>
          {modeScores.map((item, idx) => {

            const value = Number(item[score as keyof InsightResult]);
            // const color = alpha(theme.palette.primary.main, 0.4);
            const color = interpolateDivergant(
              theme.palette.score.light,
              theme.palette.score.main,
              theme.palette.score.dark,
              1 - calcPercentage(value)
            )

            return (
              <HelpBox title={`${item.label} (${formatFunc(value)})`}>
                <Box
                  key={idx}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    border: 1,
                    borderRadius: 1,
                    borderColor:theme.palette.divider,
                    py: 1,
                    px: 0.5,
                    width: 54,
                    height: 90,
                    // backgroundImage: `-webkit-linear-gradient(bottom, ${alpha(theme.palette.primary.main, 0.4)}, ${alpha(theme.palette.primary.main, 0.4)} ${calcPercentage(value) * 100}%, transparent ${calcPercentage(value) * 100}%, ${theme.palette.divider} 100%)`
                    backgroundImage: `linear-gradient(${alpha(color, 0.6)}, ${alpha(color, 0.6)})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'bottom',
                    backgroundSize: `100% ${calcPercentage(value) * 100}%`,
                    backgroundClip: 'padding-box',
                    backgroundOrigin: 'padding-box',
                  }}
                >
                  {item.icon}
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "8px",
                        textAlign: "center"
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "8px",
                        textAlign: "center"
                      }}
                    >
                      {formatFunc(value)}
                    </Typography>
                </Box>
              </HelpBox>
            )})}
        </Stack>

      </CardContent>
    </Card>
  );
};

export default ModeCard;
